import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchLyrics } from '../services/Lyricsapi';
import { selectCatalog, fetchBhajanDataRequest } from '../Features/BhajanSlice';
import { deriveBhajanDetail } from '../bhajan/deriveBhajanDetail';
import type { RootState } from '../store/store';
import { LyricsRenderer } from '../bhajan/LyricsRenderer';
import { FontSizeControl } from '../bhajan/FontSizeControl';
import { BhajanBaby } from '../bhajan/BhajanBaby';
import { MetadataTable, type MetadataRow } from '../bhajan/MetadataTable';
import { EmbeddedPlayer } from '../audio/EmbeddedPlayer';
import { useToast, Button, EmptyState, Tooltip, IconButton, cn } from '../ui';
import { BhajanGrid } from './BhajanGrid';
import { bhajanToCardData } from './BhajanCard';
import type { AnyGraphNode } from '../types/graphNodes';
import { APP } from '../app/appConfig';
import { useDetailNodes } from '../bhajan/useDetailNodes';
import { getDistractionAlways } from '../bhajan/distractionPref';
import { BhajanSettingsMenu } from '../bhajan/BhajanSettingsMenu';
import { PronunciationHint } from '../pronunciation/PronunciationHint';
import { BhajanFactsCard } from '../bhajan/BhajanFactsCard';
import { useScanBeacon } from '../bhajan/scanBeacon';
import { analyzeLyrics } from '../bhajan/lyricsDetection';
import { useDiamondView } from '../bhajan/useDiamondView';
import { defaultLyricsSettings, loadLyricsSettings, saveLyricsSettings, type LyricsSettings } from '../bhajan/lyricsSettings';
import { Link } from 'react-router-dom';

const FONT_KEY = 'lyricsFontSize';

const ShareIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);
const FocusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
    <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

const LANG_TO_CODE: Record<string, string> = {
  English: 'en', Telugu: 'te', Kannada: 'kn', Tamil: 'ta', Hindi: 'hi',
  Malayalam: 'ml', Oriya: 'or', Bengali: 'bn', Punjabi: 'pa', Gujarati: 'gu',
};

const BhajanDetails: React.FC = () => {
  const { nodeId } = useParams<{ nodeId: string }>();
  const navigate = useNavigate();
  // Count a QR scan that landed here (visitor without the app); see scanBeacon.
  useScanBeacon(nodeId, useLocation().search);
  const catalog = useSelector(selectCatalog);
  // Language comes from Redux+localStorage (LanguageSlice) — not URL.
  const selectedLanguage = useSelector((s: RootState) => s.language.selectedLanguage);
  const language = LANG_TO_CODE[selectedLanguage] || 'en';
  const toast = useToast();

  const dispatch = useDispatch();

  const [lyrics, setLyrics] = useState<string | null>(null);
  const [lyricsLoading, setLyricsLoading] = useState(true);
  const [fontSize, setFontSize] = useState<number>(() => {
    const v = parseInt(localStorage.getItem(FONT_KEY) || '', 10);
    return Number.isFinite(v) && v >= 14 && v <= 36 ? v : 22;
  });
  const [lyricsSettings, setLyricsSettingsState] = useState<LyricsSettings>(() => {
    if (typeof window === 'undefined') return defaultLyricsSettings;
    return loadLyricsSettings();
  });
  const updateLyricsSettings = useCallback((next: LyricsSettings) => {
    setLyricsSettingsState(next);
    saveLyricsSettings(next);
  }, []);

  // Diamond (vajra) layout is on by default and resets per bhajan, not per language.
  const { showingDiamondView, toggleDiamondView } = useDiamondView(nodeId);
  const lyricsAnalysis = useMemo(() => analyzeLyrics(lyrics ?? ''), [lyrics]);

  const handleFontChange = useCallback((next: number) => {
    setFontSize(next);
    localStorage.setItem(FONT_KEY, String(next));
  }, []);

  // Auto-route to distraction mode if user has the always-on preference
  useEffect(() => {
    if (getDistractionAlways() && nodeId) {
      navigate(`/distraction/nodes/${nodeId}`, { replace: true });
    }
  }, [nodeId, navigate]);

  // The detail page renders entirely from the content catalog (allNonLyrics),
  // which the app loads once into Redux. On a cold direct navigation the
  // catalog may not be loaded yet, so kick off the fetch.
  useEffect(() => {
    if (!catalog) dispatch(fetchBhajanDataRequest());
  }, [catalog, dispatch]);

  // Slim catalog (for `related` below — it needs every bhajan).
  const nodes = useMemo(
    () => (catalog?.nodes ?? []),
    [catalog]
  );
  // Full data for THIS bhajan's neighbourhood (talam.beatPattern, audio, bb, …)
  // — hydrated on demand since the slim index omits detail fields.
  const { nodes: detailNodes, loading: detailLoading } = useDetailNodes(Number(nodeId));
  const vm = useMemo(
    () => deriveBhajanDetail(detailNodes, Number(nodeId), language),
    [detailNodes, nodeId, language]
  );
  const {
    bhajanId, title, ragam, ragamId, talam, talamId, beatPattern,
    deity, deityId, composer, composerId, audio: audioSrc, bhajanBabyYouTube,
    westernNotesNodeId, facts,
  } = vm;
  const language_ = vm.language;

  const catalogLoaded = !!catalog;
  const metaLoading = !catalogLoaded || detailLoading;
  const notFound = catalogLoaded && !detailLoading && !vm.found;

  // Lyrics text is the one thing NOT in the catalog (LYRICS nodes are excluded
  // from allNonLyrics), so it's fetched on demand. Best-effort: a failure just
  // shows the graceful "not available" message.
  useEffect(() => {
    if (!catalogLoaded || !vm.found) return;
    const lyricsId = vm.lyricsNodeId;
    if (!lyricsId) { setLyrics('Lyrics not available.'); setLyricsLoading(false); return; }

    let cancelled = false;
    setLyrics(null);
    setLyricsLoading(true);
    fetchLyrics(String(lyricsId), language)
      .then((ld) => {
        if (!cancelled) { setLyrics(ld.data?.text || 'Lyrics not available.'); setLyricsLoading(false); }
      })
      .catch(() => {
        if (!cancelled) { setLyrics('Lyrics not available.'); setLyricsLoading(false); }
      });
    return () => { cancelled = true; };
  }, [catalogLoaded, vm.found, vm.lyricsNodeId, language]);

  // Related bhajans (same ragam) — derived from the same catalog.
  const related = useMemo(() => {
    if (!bhajanId || nodes.length === 0) return [];
    const me = nodes.find((n) => n.nodeId === bhajanId);
    if (!me) return [];
    const ragamEdge = me.edges?.find((e) => e.name === 'ragam');
    if (!ragamEdge) return [];
    return nodes
      .filter((n) => n.nodeTypeId === 1 && n.nodeId !== bhajanId)
      .filter((n) => n.edges?.some((e) => e.name === 'ragam' && e.nodeId === ragamEdge.nodeId))
      .slice(0, 8)
      .map((n) => bhajanToCardData(n, nodes));
  }, [nodes, bhajanId]);

  const handleShare = useCallback(async () => {
    const url = window.location.href;
    const text = title ?? 'Bhajan';
    if (navigator.share) {
      try { await navigator.share({ title: text, url }); return; } catch { /* fall through */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.show('Link copied', { type: 'success' });
    } catch {
      toast.show('Could not copy link', { type: 'error' });
    }
  }, [title, toast]);

  // Sync with global audio player when user switches services to "preview"
  useEffect(() => {
    if (audioSrc?.previewUrl && bhajanId) {
      // We don't auto-enqueue; the user can use the embed's own play button.
      // The AudioPlayerProvider stays available for other surfaces.
    }
  }, [audioSrc, bhajanId]);

  // "stotram" / "mantra" as a deity name is a placeholder used by the data
  // model when a bhajan is actually a stotra or mantra; surfacing it as a
  // deity is confusing, so we suppress that row entirely.
  const isPlaceholderDeity = (() => {
    const n = deity?.trim().toLowerCase();
    return n === 'stotram' || n === 'stotra' || n === 'mantra';
  })();

  const metadataRows: MetadataRow[] = [
    { label: 'Ragam', value: ragam, edgeName: 'ragam', edgeNodeId: ragamId ?? undefined },
    { label: 'Talam', value: talam, edgeName: 'talam', edgeNodeId: talamId ?? undefined },
    ...(isPlaceholderDeity
      ? []
      : [{ label: 'Deity', value: deity, edgeName: 'deity', edgeNodeId: deityId ?? undefined }]),
    { label: 'Composer', value: composer, edgeName: 'composer', edgeNodeId: composerId ?? undefined },
    { label: 'Language', value: language_ },
  ];

  if (notFound) {
    return (
      <main id="main">
        <EmptyState
          title="Bhajan not found"
          description="We couldn't find this bhajan."
          action={<Button variant="primary" onClick={() => navigate('/bhajans')}>Browse all bhajans</Button>}
        />
      </main>
    );
  }

  return (
    <main id="main">
      <article className="mx-auto max-w-5xl px-4 sm:px-6 pt-8 sm:pt-12 pb-10 bhajan-print-root">
        {/* HEADER ROW — Bhajan ID (left) + share / distraction icons (right). */}
        <header className="flex items-start justify-between gap-4 mb-6">
          <div className="font-mono text-h1 sm:text-display font-semibold text-accent-gold leading-none tabular-nums tracking-tight">
            #{bhajanId ?? '…'}
          </div>
          <div className="flex items-center gap-2 print-hide">
            <Tooltip content="Share" side="bottom">
              <IconButton ariaLabel="Share" variant="ghost" onClick={handleShare} icon={<ShareIcon />} />
            </Tooltip>
            <Tooltip content="Distraction-free mode" side="bottom">
              <IconButton
                ariaLabel="Distraction-free mode"
                variant="ghost"
                onClick={() => navigate(`/distraction/nodes/${nodeId}`)}
                icon={<FocusIcon />}
              />
            </Tooltip>
            <BhajanSettingsMenu
              settings={lyricsSettings}
              onChange={updateLyricsSettings}
              onCopyAsText={async () => {
                if (!lyrics) return;
                try {
                  await navigator.clipboard.writeText(lyrics);
                  toast.show('Lyrics copied', { type: 'success' });
                } catch {
                  toast.show('Could not copy lyrics', { type: 'error' });
                }
              }}
            />
          </div>
        </header>

        {/* TITLE — full width */}
        <h1
          className={cn(
            'font-display font-semibold text-text-primary leading-tight mb-8 capitalize',
            'text-h2 sm:text-h1 lg:text-display'
          )}
        >
          {title ?? <span className="inline-block h-10 w-72 max-w-full rounded-md bg-bg-3 animate-pulse align-middle" />}
        </h1>

        {/*
          Contextual entry to the pronunciation guide — the point of confusion.
          Only for English (IAST) readers. One-time banner, then a quiet link.
        */}
        <PronunciationHint />

        {/*
          Print-only banner — sits prominently right below the title so the
          first thing a reader sees on the printed sheet (after the bhajan
          name) is the offline-app pitch. Hidden on screen.
        */}
        {APP.features.appDownload && (
          <aside className="print-only-banner" aria-hidden>
            <div className="print-only-banner__inner">
              <h2 className="print-only-banner__title">Save mother earth — get the Bhajan Mala app</h2>
              <p className="print-only-banner__body">
                Offline access to every bhajan, lyrics in 10 languages, and live
                satsang. Available for iOS and Android.
              </p>
            </div>
          </aside>
        )}

        {/*
          ASIDE LAYOUT — adaptive to which content blocks actually exist.
            Possible blocks: metadata, babies, player. Each is only included
            when it has real content (e.g. metadata is suppressed when every
            row is empty, so we don't ship a blank left column).

              0 blocks → nothing rendered
              1 block  → full-width, capped to a sensible reading column
              2 blocks → md+: side-by-side; mobile: stacked
              3 blocks → md+: metadata+babies stacked left, player right
        */}
        {(() => {
          const hasPlayer = !!(audioSrc?.appleTrackId || audioSrc?.spotifyTrackId || audioSrc?.previewUrl);
          // Shown for any bhajan that actually has a Bhajan-Babies YouTube link via its
          // `bb` edge, in every tenant. Only the standalone Bhajan Babies section is
          // tenant-gated (bhajanBabiesSection) — a bhajan that has one still shows it.
          const hasBabies = !!bhajanBabyYouTube && !!deity;
          // Hide the metadata block entirely when there's nothing to show; the
          // alternative is an empty card next to the player, which looks broken.
          const hasMetadata = metaLoading || metadataRows.some((r) => r.value && String(r.value).trim());

          const playerEl = hasPlayer ? (
            <div className="print-hide">
              <EmbeddedPlayer
                sources={{
                  appleTrackId: audioSrc!.appleTrackId,
                  appleCollectionId: audioSrc!.appleCollectionId,
                  spotifyTrackId: audioSrc!.spotifyTrackId,
                  previewUrl: audioSrc!.previewUrl,
                  durationMs: audioSrc!.durationMs,
                  title: title ?? undefined,
                  artist: composer ?? undefined,
                }}
              />
            </div>
          ) : null;
          const babiesEl = hasBabies ? (
            <div className="print-hide">
              <BhajanBaby deityName={deity!} size="card" youtubeUrl={bhajanBabyYouTube} title={title ?? undefined} />
            </div>
          ) : null;
          const metadataEl = hasMetadata ? <MetadataTable rows={metadataRows} loading={metaLoading} /> : null;

          // Three blocks: native layout — metadata + babies stacked left, player right.
          if (hasMetadata && hasPlayer && hasBabies) {
            return (
              <div className="grid md:grid-cols-2 gap-5 md:gap-6 mb-10 items-start">
                <div className="flex flex-col gap-5 min-w-0 order-1">
                  {metadataEl}
                  {babiesEl}
                </div>
                <div className="min-w-0 order-2">{playerEl}</div>
              </div>
            );
          }

          // Two blocks: side-by-side on md+, stacked on mobile.
          const blocks = [metadataEl, babiesEl, playerEl].filter(Boolean);
          if (blocks.length === 2) {
            return (
              <div className="grid md:grid-cols-2 gap-5 md:gap-6 mb-10 items-start">
                <div className="min-w-0">{blocks[0]}</div>
                <div className="min-w-0">{blocks[1]}</div>
              </div>
            );
          }

          // One block (or zero): single column, capped width so a lone player
          // or metadata table doesn't sprawl across the whole page.
          if (blocks.length === 1) {
            return <div className="mb-10 max-w-2xl">{blocks[0]}</div>;
          }
          return null;
        })()}

        {/* WESTERN NOTES entry — edge-gated: shown only when this bhajan
            actually has a score. Deep-linkable dedicated route so printing gets
            its own pagination, not the lyric-sheet print CSS. */}
        {westernNotesNodeId && (
          <section aria-label="Western notation" className="mb-10 print-hide">
            {/* Styled with the site's own tokens, NOT the viewer stylesheet:
                this page is in the main chunk, so importing westernNotes.css
                here would ship the whole .wn-* ruleset (and its global
                --sheet-* custom properties) in main.css for every visitor,
                including the overwhelming majority who never open a score. The
                viewer imports its own stylesheet inside the lazy chunk. */}
            <Link
              to={`/bhajan/${nodeId}/western-notes`}
              className="flex items-center gap-3 px-5 py-4 rounded-2xl bg-bg-2 border border-light text-text-primary no-underline transition-colors duration-150 hover:bg-bg-3/60"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M9 18V5l12-2v13" />
                <circle cx="6" cy="18" r="3" />
                <circle cx="18" cy="16" r="3" />
              </svg>
              <span>
                <span className="block font-semibold">Western Notes</span>
                <span className="block text-sm text-text-secondary">
                  Sheet music with playback, tempo control and practice loop
                </span>
              </span>
            </Link>
          </section>
        )}

        {/* DID YOU KNOW — surfaces the diamond/acrostic structure of this bhajan and
            owns the diamond-layout toggle, so the button sits directly above the
            thing it changes. Renders null unless the bhajan is one of the two special
            shapes, so no conditional is needed here. The page has a single layout
            tree (the mobile/desktop split is Tailwind's `grid md:grid-cols-2`, not a
            JS branch), so this is the only insertion point. */}
        <BhajanFactsCard
          hasDiamondSection={lyricsAnalysis.hasDiamondSection}
          hasAcrosticMarkers={lyricsAnalysis.hasAcrosticMarkers}
          showingDiamondView={showingDiamondView}
          onToggleDiamondView={toggleDiamondView}
          facts={facts}
          className="mb-8 print-hide"
        />

        {/* LYRICS — full width so long lines don't wrap awkwardly */}
        <section aria-label="Lyrics">
          <div className="mb-4 print-hide">
            <FontSizeControl value={fontSize} onChange={handleFontChange} />
          </div>
          <LyricsRenderer
            text={lyrics}
            isLoading={lyricsLoading}
            fontSize={fontSize}
            beatPattern={beatPattern}
            settings={lyricsSettings}
            lyricsAnalysis={lyricsAnalysis}
            showingDiamondView={showingDiamondView}
            languageCode={language}
          />
        </section>
      </article>

      {related.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 py-10 border-t border-light print-hide">
          <h2 className="text-h2 font-display font-semibold mb-6">More in this ragam</h2>
          <BhajanGrid bhajans={related} language={language} density="comfortable" />
        </section>
      )}
    </main>
  );
};

export default BhajanDetails;
